import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
    LayoutDashboard, LayoutGrid, TreeDeciduous, Images, Lock, Scale, Users, Heart,
    CalendarDays, Settings as SettingsIcon, User, CreditCard, UserPlus, Mic, BarChart3,
    Activity, PartyPopper, LifeBuoy, ArrowRight,
} from "lucide-react";
import {
    CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from "@/components/ui/command";

const QUICK_PAGES = [
    { id: "dashboard", title: "Dashboard", route: "/dashboard", icon: LayoutDashboard },
    { id: "family-hub", title: "Family Hub (all features)", route: "/family", icon: LayoutGrid },
    { id: "family-tree", title: "Family Tree", route: "/family-tree", icon: TreeDeciduous },
    { id: "memories", title: "Photo Memories", route: "/memories", icon: Images },
    { id: "vault", title: "Document Vault", route: "/vault", icon: Lock },
    { id: "legal-vault", title: "Legal Vault", route: "/legal-vault", icon: Scale },
    { id: "members", title: "Members Directory", route: "/members", icon: Users },
    { id: "marriages", title: "Marriages", route: "/marriages", icon: Heart },
    { id: "calendar", title: "Family Calendar", route: "/calendar", icon: CalendarDays },
    { id: "record-story", title: "Record a Story", route: "/record-story", icon: Mic },
    { id: "invite", title: "Invite Family", route: "/invite", icon: UserPlus },
    { id: "statistics", title: "Family Statistics", route: "/statistics", icon: BarChart3 },
    { id: "activity", title: "Family Activity", route: "/activity", icon: Activity },
    { id: "celebrations", title: "Celebrations", route: "/celebrations", icon: PartyPopper },
    { id: "plans", title: "Plans & Subscription", route: "/plans", icon: CreditCard },
    { id: "profile", title: "My Profile", route: "/profile", icon: User },
    { id: "settings", title: "Settings", route: "/settings", icon: SettingsIcon },
    { id: "support", title: "Support Center", route: "/support", icon: LifeBuoy },
];

export const openCommandPalette = () => window.dispatchEvent(new Event("vt-open-palette"));

export const CommandPalette = () => {
    const [open, setOpen] = useState(false);
    const [categories, setCategories] = useState([]);
    const loadedRef = useRef(false);
    const navigate = useNavigate();

    const loadCatalog = useCallback(async () => {
        if (loadedRef.current) return;
        loadedRef.current = true;
        try {
            const mod = await import("@/pages/FamilyHub");
            setCategories(mod.CATEGORIES || []);
        } catch { /* catalog optional */ }
    }, []);

    useEffect(() => {
        const onKey = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                setOpen((o) => !o);
            }
        };
        const onOpen = () => setOpen(true);
        window.addEventListener("keydown", onKey);
        window.addEventListener("vt-open-palette", onOpen);
        return () => {
            window.removeEventListener("keydown", onKey);
            window.removeEventListener("vt-open-palette", onOpen);
        };
    }, []);

    useEffect(() => { if (open) loadCatalog(); }, [open, loadCatalog]);

    const go = (route) => {
        setOpen(false);
        navigate(route);
    };

    return (
        <CommandDialog open={open} onOpenChange={setOpen}>
            <CommandInput placeholder="Search 70+ features… (Ctrl+K)" data-testid="command-palette-input" />
            <CommandList className="max-h-[60vh]" data-testid="command-palette-list">
                <CommandEmpty>No feature found.</CommandEmpty>
                <CommandGroup heading="Go to">
                    {QUICK_PAGES.map((p) => {
                        const Icon = p.icon;
                        return (
                            <CommandItem
                                key={p.id}
                                value={`goto ${p.title}`}
                                onSelect={() => go(p.route)}
                                data-testid={`command-item-${p.id}`}
                                className="gap-2.5 cursor-pointer"
                            >
                                <Icon className="text-[#1B5E20]" />
                                <span>{p.title}</span>
                            </CommandItem>
                        );
                    })}
                </CommandGroup>
                {categories.map((cat) => (
                    <CommandGroup key={cat.id} heading={cat.title}>
                        {(cat.items || []).map((it) => {
                            const Icon = it.icon || ArrowRight;
                            const live = it.status === "live" && it.route;
                            return (
                                <CommandItem
                                    key={it.id}
                                    value={`${it.title} ${cat.title}`}
                                    onSelect={() => go(live ? it.route : "/family")}
                                    data-testid={`command-item-${it.id}`}
                                    className="gap-2.5 cursor-pointer"
                                >
                                    <Icon className="text-[#4A5D4E]" />
                                    <span className="flex-1 min-w-0 truncate">{it.title}</span>
                                    {!live && (
                                        <span className="text-[10px] font-bold uppercase tracking-wide text-[#F57C00] bg-[#FFF3E0] rounded-full px-2 py-0.5 shrink-0">
                                            Soon
                                        </span>
                                    )}
                                </CommandItem>
                            );
                        })}
                    </CommandGroup>
                ))}
            </CommandList>
        </CommandDialog>
    );
};
